export type FishIQWaterbodyProfile = {
  id: string;
  name: string;
  aliases?: string[];
  generalizedCenter: { lat: number; lon: number };
  matchRadiusKm: number;
  waterKind: 'freshwater' | 'brackish' | 'saltwater';
  waterTypes: string[];
  habitatZones: string[];
  likelySpeciesIds: string[];
  seasonalSpeciesIds?: string[];
  stockedSpeciesIds?: string[];
  excludedSpeciesIds?: string[];
  sourceRefs: string[];
  status: 'editorial_seed' | 'agency_verified' | 'community_supported';
  verifiedAt?: number;
};

// Conservative launch profiles. These are generalized waterbody-level hints,
// never exact spots, and cannot independently create a Strong local match.
export const FISHIQ_WATERBODY_PROFILES: FishIQWaterbodyProfile[] = [
  {
    id: 'diamond_valley_lake_ca',
    name: 'Diamond Valley Lake',
    generalizedCenter: { lat: 33.68, lon: -117.03 },
    matchRadiusKm: 11,
    waterKind: 'freshwater',
    waterTypes: ['lake', 'reservoir'],
    habitatZones: ['stillwater', 'deep-edge', 'hard-structure'],
    likelySpeciesIds: ['largemouth_bass', 'smallmouth_bass', 'striped_bass', 'bluegill', 'channel_catfish', 'common_carp'],
    stockedSpeciesIds: ['rainbow_trout'],
    sourceRefs: ['roddo-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'lake_perris_ca',
    name: 'Lake Perris',
    generalizedCenter: { lat: 33.86, lon: -117.18 },
    matchRadiusKm: 9,
    waterKind: 'freshwater',
    waterTypes: ['lake', 'reservoir'],
    habitatZones: ['stillwater', 'vegetation', 'hard-structure'],
    likelySpeciesIds: ['largemouth_bass', 'bluegill', 'redear_sunfish', 'channel_catfish', 'common_carp'],
    stockedSpeciesIds: ['rainbow_trout'],
    sourceRefs: ['roddo-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'big_bear_lake_ca',
    name: 'Big Bear Lake',
    generalizedCenter: { lat: 34.25, lon: -116.91 },
    matchRadiusKm: 12,
    waterKind: 'freshwater',
    waterTypes: ['lake'],
    habitatZones: ['stillwater', 'deep-edge'],
    likelySpeciesIds: ['rainbow_trout', 'largemouth_bass', 'smallmouth_bass', 'bluegill', 'channel_catfish', 'common_carp'],
    sourceRefs: ['roddo-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'castaic_lake_ca',
    name: 'Castaic Lake',
    generalizedCenter: { lat: 34.55, lon: -118.61 },
    matchRadiusKm: 12,
    waterKind: 'freshwater',
    waterTypes: ['lake', 'reservoir'],
    habitatZones: ['stillwater', 'deep-edge', 'hard-structure'],
    likelySpeciesIds: ['largemouth_bass', 'smallmouth_bass', 'striped_bass', 'channel_catfish', 'bluegill'],
    stockedSpeciesIds: ['rainbow_trout'],
    sourceRefs: ['roddo-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'long_beach_harbor_ca',
    name: 'Long Beach Harbor',
    aliases: ['Long Beach Harbor Area', 'Los Angeles Harbor'],
    generalizedCenter: { lat: 33.75, lon: -118.19 },
    matchRadiusKm: 16,
    waterKind: 'saltwater',
    waterTypes: ['harbor', 'bay', 'inshore'],
    habitatZones: ['bay-harbor', 'manmade-structure', 'hard-structure'],
    likelySpeciesIds: ['spotted_bay_bass', 'barred_sand_bass', 'halibut', 'yellowfin_croaker', 'pacific_mackerel', 'pacific_bonito'],
    sourceRefs: ['roddo-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'newport_bay_ca',
    name: 'Newport Bay',
    aliases: ['Upper Newport Bay', 'Lower Newport Bay'],
    generalizedCenter: { lat: 33.62, lon: -117.9 },
    matchRadiusKm: 12,
    waterKind: 'brackish',
    waterTypes: ['bay', 'harbor', 'estuary'],
    habitatZones: ['bay-harbor', 'estuary-wetland', 'manmade-structure'],
    likelySpeciesIds: ['spotted_bay_bass', 'halibut', 'yellowfin_croaker', 'barred_sand_bass'],
    sourceRefs: ['roddo-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'mission_bay_ca',
    name: 'Mission Bay',
    generalizedCenter: { lat: 32.78, lon: -117.23 },
    matchRadiusKm: 12,
    waterKind: 'saltwater',
    waterTypes: ['bay', 'harbor', 'inshore'],
    habitatZones: ['bay-harbor', 'vegetation', 'manmade-structure'],
    likelySpeciesIds: ['spotted_bay_bass', 'halibut', 'yellowfin_croaker', 'barred_sand_bass'],
    sourceRefs: ['roddo-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'san_diego_bay_ca',
    name: 'San Diego Bay',
    generalizedCenter: { lat: 32.68, lon: -117.15 },
    matchRadiusKm: 18,
    waterKind: 'saltwater',
    waterTypes: ['bay', 'harbor', 'inshore'],
    habitatZones: ['bay-harbor', 'hard-structure', 'manmade-structure'],
    likelySpeciesIds: ['spotted_bay_bass', 'barred_sand_bass', 'halibut', 'yellowfin_croaker', 'pacific_bonito', 'pacific_mackerel'],
    sourceRefs: ['roddo-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'laguna_de_bay_ph',
    name: 'Laguna de Bay',
    aliases: ['Laguna Lake'],
    generalizedCenter: { lat: 14.31, lon: 121.24 },
    matchRadiusKm: 38,
    waterKind: 'freshwater',
    waterTypes: ['lake'],
    habitatZones: ['stillwater', 'vegetation', 'deep-edge'],
    likelySpeciesIds: ['mudfish_dalag', 'striped_snakehead', 'nile_tilapia', 'common_carp', 'walking_catfish'],
    sourceRefs: ['roddo-ph-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'taal_lake_ph',
    name: 'Taal Lake',
    generalizedCenter: { lat: 13.98, lon: 120.99 },
    matchRadiusKm: 18,
    waterKind: 'freshwater',
    waterTypes: ['lake'],
    habitatZones: ['stillwater', 'deep-edge'],
    likelySpeciesIds: ['nile_tilapia', 'mudfish_dalag', 'common_carp'],
    sourceRefs: ['roddo-ph-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'manila_bay_ph',
    name: 'Manila Bay',
    generalizedCenter: { lat: 14.45, lon: 120.75 },
    matchRadiusKm: 42,
    waterKind: 'brackish',
    waterTypes: ['bay', 'estuary', 'inshore'],
    habitatZones: ['bay-harbor', 'estuary-wetland', 'manmade-structure'],
    likelySpeciesIds: ['milkfish', 'mangrove_jack', 'philippines_talakitok', 'threadfin'],
    sourceRefs: ['roddo-ph-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'nagarjuna_sagar_india',
    name: 'Nagarjuna Sagar Reservoir',
    aliases: ['Nagarjuna Sagar'],
    generalizedCenter: { lat: 16.58, lon: 79.31 },
    matchRadiusKm: 34,
    waterKind: 'freshwater',
    waterTypes: ['reservoir', 'lake'],
    habitatZones: ['stillwater', 'deep-edge', 'flowing-water'],
    likelySpeciesIds: ['rohu_india', 'catla_india', 'mrigal_india', 'common_carp', 'tilapia_india'],
    sourceRefs: ['roddo-india-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'vembanad_india',
    name: 'Vembanad Lake',
    aliases: ['Vembanad', 'Vembanad Backwaters', 'Kerala Backwaters'],
    generalizedCenter: { lat: 9.62, lon: 76.39 },
    matchRadiusKm: 42,
    waterKind: 'brackish',
    waterTypes: ['backwater', 'estuary', 'lake'],
    habitatZones: ['estuary-wetland', 'vegetation', 'shallow-flat'],
    likelySpeciesIds: ['pearlspot_india', 'barramundi_india', 'fourfinger_threadfin_india', 'mangrove_jack_india'],
    sourceRefs: ['roddo-india-waterbody-seed-v1'],
    status: 'editorial_seed',
  },
  {
    id: 'chilika_lagoon_india',
    name: 'Chilika Lake',
    aliases: ['Chilika Lagoon', 'Chilika'],
    generalizedCenter: { lat: 19.72, lon: 85.36 },
    matchRadiusKm: 42,
    waterKind: 'brackish',
    waterTypes: ['estuary', 'bay', 'backwater'],
    habitatZones: ['estuary-wetland', 'shallow-flat'],
    likelySpeciesIds: ['hilsa_india', 'barramundi_india', 'fourfinger_threadfin_india'],
    sourceRefs: ['roddo-india-waterbody-seed-v1'],
    status: 'editorial_seed',
  },

];

const lower = (value: unknown) => String(value ?? '').trim().toLowerCase();

function haversineKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const R = 6371;
  const toRad = (value: number) => value * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function findFishIQWaterbodyProfile(input: {
  lat?: number | null;
  lon?: number | null;
  waterName?: string | null;
}): FishIQWaterbodyProfile | null {
  const name = lower(input.waterName);
  const byName = FISHIQ_WATERBODY_PROFILES.find((profile) =>
    [profile.name, ...(profile.aliases || [])].some((value) => {
      const candidate = lower(value);
      return candidate && (name.includes(candidate) || candidate.includes(name));
    }),
  );
  if (byName) return byName;

  if (!Number.isFinite(input.lat) || !Number.isFinite(input.lon)) return null;
  const point = { lat: Number(input.lat), lon: Number(input.lon) };
  return FISHIQ_WATERBODY_PROFILES
    .map((profile) => ({ profile, distance: haversineKm(point, profile.generalizedCenter) }))
    .filter((row) => row.distance <= row.profile.matchRadiusKm)
    .sort((a, b) => a.distance - b.distance)[0]?.profile || null;
}

export function getFishIQWaterbodySpeciesSignal(profile: FishIQWaterbodyProfile | null, species: any) {
  if (!profile) return null;
  const ids = new Set([
    String(species?.id || '').toLowerCase(),
    String(species?.slug || '').replace(/-/g, '_').toLowerCase(),
    String(species?.canonicalSpecies?.canonicalId || '').toLowerCase(),
    ...((species?.canonicalSpecies?.legacyIds || []).map((id: string) => String(id).toLowerCase())),
  ]);
  const matches = (values: string[] | undefined) => (values || []).some((id) => ids.has(String(id).toLowerCase()));
  if (matches(profile.excludedSpeciesIds)) return { kind: 'excluded' as const, score: -140, label: 'Waterbody exclusion' };
  if (matches(profile.stockedSpeciesIds)) return { kind: 'stocked' as const, score: 13, label: 'Known stocked profile' };
  if (matches(profile.seasonalSpeciesIds)) return { kind: 'seasonal' as const, score: 10, label: 'Seasonal waterbody profile' };
  if (matches(profile.likelySpeciesIds)) return { kind: 'likely' as const, score: 15, label: 'Waterbody profile match' };
  return null;
}
